"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { type ComponentProps, forwardRef, type ReactNode } from "react";
import {
	FieldError,
	Group,
	Label,
	Input as RACInput,
	type InputProps as RACInputProps,
	Text,
	TextField,
	type TextFieldProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useTranslations } from "@/i18n/client";

const fieldStyles = cva(
	[
		"flex items-center w-full",
		"border border-solid",
		"rounded-radius-component-specific-input",
		"transition-colors duration-200",
		"bg-input-default-bg",
		"data-[disabled]:bg-input-disabled-bg",
	],
	{
		variants: {
			size: {
				small: "h-8 pl-xs pr-3-xs py-3-xs",
				medium: "h-10 pl-xs pr-3-xs py-3-xs gap-2-xs",
				large: "h-12 pl-xs pr-2-xs py-2-xs gap-2-xs",
				"extra-large": "h-16 pl-xs pr-xs py-xs gap-2-xs",
			},
			tone: {
				default: [
					"border-input-default-border",
					"data-[hovered]:border-input-hover-border",
					"data-[focus-within]:border-input-active-border",
					"data-[disabled]:border-input-disabled-border",
				],
				error: "border-input-error-border",
				success: "border-input-success-border",
			},
		},
		defaultVariants: {
			size: "medium",
			tone: "default",
		},
	},
);

const inputStyles = cva(
	[
		"flex-1 min-w-0 bg-transparent outline-none",
		"font-regular",
		"placeholder:text-text-dark-text2",
		"data-[disabled]:text-text-dark-text2 data-[disabled]:cursor-not-allowed",
	],
	{
		variants: {
			size: {
				small: "font-body text-base leading-[1.15]",
				medium: "font-body text-base leading-[1.15]",
				large: "font-body text-base leading-[1.15]",
				"extra-large": "font-body text-lg leading-[1.5]",
			},
			textTone: {
				default: "text-text-dark-text1",
				error: "text-status-error-text placeholder:text-status-error-text",
				success: "text-status-success-text",
			},
		},
		defaultVariants: {
			size: "medium",
			textTone: "default",
		},
	},
);

const messageStyles = cva("font-body text-xs leading-[1.5]", {
	variants: {
		tone: {
			default: "text-text-dark-text1",
			error: "text-status-error-text",
			success: "text-status-success-text",
		},
	},
	defaultVariants: { tone: "default" },
});

type FieldVariants = VariantProps<typeof fieldStyles>;

export type InputProps = Omit<ComponentProps<"input">, "size" | "className"> & {
	size?: NonNullable<FieldVariants["size"]>;
	label?: ReactNode;
	isOptional?: boolean;
	description?: ReactNode;
	errorMessage?: ReactNode;
	successMessage?: ReactNode;
	isInvalid?: boolean;
	isSuccess?: boolean;
	action?: ReactNode;
	className?: string;
	fieldClassName?: string;
	inputClassName?: string;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			size = "medium",
			label,
			isOptional = false,
			description,
			errorMessage,
			successMessage,
			isInvalid,
			isSuccess = false,
			action,
			placeholder,
			className,
			fieldClassName,
			inputClassName,
			onChange,
			value,
			name,
			disabled,
			...inputProps
		},
		ref,
	) => {
		const t = useTranslations("form");
		const tone: "default" | "error" | "success" = isInvalid
			? "error"
			: isSuccess
				? "success"
				: "default";

		const showAction = size !== "small" && action != null;

		// Bridge React Aria's onChange(value: string) to React Hook Form's onChange(event)
		const handleChange = onChange
			? (value: string) => {
					// Create a synthetic event that React Hook Form expects
					const syntheticEvent = {
						target: {
							name: name || "",
							value: value,
						},
					} as React.ChangeEvent<HTMLInputElement>;
					onChange(syntheticEvent);
				}
			: undefined;

		return (
			<TextField
				name={name}
				isDisabled={disabled}
				isInvalid={isInvalid}
				value={value as string | undefined}
				onChange={handleChange}
				className={twMerge("flex flex-col gap-2-xs w-full", className)}
			>
				{label != null && (
					<div className="flex gap-3-xs items-start w-full">
						<Label className="flex-1 font-body font-regular text-xs uppercase leading-[1.15] text-text-dark-text1 tabular-nums lining-nums">
							{label}
						</Label>
						{isOptional && (
							<span className="font-body font-regular text-xs uppercase leading-[1.15] text-text-dark-text2 tabular-nums lining-nums">
								{t("optional")}
							</span>
						)}
					</div>
				)}

				<Group className={twMerge(fieldStyles({ size, tone }), fieldClassName)}>
					<RACInput
						ref={ref}
						placeholder={placeholder}
						className={twMerge(
							inputStyles({ size, textTone: tone }),
							size === "extra-large" && "text-center",
							inputClassName,
						)}
						{...inputProps}
					/>
					{showAction && <div className="shrink-0">{action}</div>}
				</Group>

				{tone === "error" && errorMessage != null ? (
					<FieldError className={messageStyles({ tone: "error" })}>{errorMessage}</FieldError>
				) : tone === "success" && successMessage != null ? (
					<Text slot="description" className={messageStyles({ tone: "success" })}>
						{successMessage}
					</Text>
				) : description != null ? (
					<Text slot="description" className={messageStyles({ tone: "default" })}>
						{description}
					</Text>
				) : null}
			</TextField>
		);
	},
);

Input.displayName = "Input";
